import React from 'react';

import { useAppConfig } from '../../context/AppConfigContext';

/** A selected recipe the backend had to adapt for stock: served in its other size, or
 *  replaced by more units of the remaining recipes. */
export type StockAdjustment =
  | { id: string; name: string; kind: 'size'; sizeG: number; otherSizeG: number }
  | { id: string; name: string; kind: 'dropped' };

interface StockAdjustmentsNoticeProps {
  items: StockAdjustment[];
  /** Extra sentence before the link back to the recipe picker (e.g. "revísalo y vuelve a confirmar"). */
  footer?: string;
  /** When given, closes with "Si lo prefieres, puedes volver atrás y seleccionar otras recetas." */
  onChangeRecipes?: () => void;
  className?: string;
}

/** Shared stock notice of the funnel (step 12, checkout entry and checkout submit): one
 *  header, one line per adapted recipe with its own reason, and the way back to the picker. */
export function StockAdjustmentsNotice({ items, footer, onChangeRecipes, className }: StockAdjustmentsNoticeProps) {
  const { t } = useAppConfig();
  if (items.length === 0) return null;

  const intro =
    items.length === 1
      ? t(
          'stock.notice.intro_single',
          'Con la selección actual, tenemos que adaptar la siguiente receta por falta de stock:',
        )
      : t(
          'stock.notice.intro_plural',
          'Con la selección actual, tenemos que adaptar las siguientes recetas por falta de stock:',
        );

  return (
    <section
      role="status"
      aria-label={t('stock.notice.aria', 'Recetas adaptadas por falta de stock')}
      className={`rounded-2xl border border-amber-200 bg-amber-50 p-3 text-left sm:p-4 ${className ?? ''}`}
    >
      <p className="font-poppins text-xs leading-relaxed text-amber-900">{intro}</p>
      <ul className="mt-1 space-y-1">
        {items.map((item) => (
          <li key={item.id} className="font-poppins text-xs leading-relaxed text-amber-900">
            <strong className="font-semibold">{item.name}</strong>
            {': '}
            {item.kind === 'size'
              ? t(
                  'stock.notice.size',
                  'te la serviremos en formato {size}g porque no tenemos stock suficiente en formato {other}g.',
                  { size: item.sizeG, other: item.otherSizeG },
                )
              : t(
                  'stock.notice.dropped',
                  'te la sustituimos añadiendo más cantidad a las otras recetas seleccionadas porque no tenemos stock suficiente.',
                )}
          </li>
        ))}
      </ul>
      {(footer || onChangeRecipes) && (
        <p className="mt-2 font-poppins text-xs leading-relaxed text-amber-900">
          {footer ? `${footer} ` : ''}
          {onChangeRecipes && (
            <>
              {t('stock.notice.change_before', 'Si lo prefieres, puedes volver atrás y ')}
              <button
                type="button"
                onClick={onChangeRecipes}
                className="font-semibold text-[#008BBC] underline underline-offset-2"
              >
                {t('stock.notice.change_link', 'seleccionar otras recetas')}
              </button>
              {t('stock.notice.change_after', '.')}
            </>
          )}
        </p>
      )}
    </section>
  );
}
